import { useContext, useEffect, useState } from 'react';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { IconButton } from '@mui/material';
import {
  APP_ROUTES,
  FlyerType,
  OrgType,
  SubscriptionTypeEnum,
} from '@/config/constants';
import { BodyLargeEmphasized, BodySmall } from '@/design-system';
import mySchoolsIcon from '@/assets/my_schools.svg';
import schoolDefaultIcon from '@/assets/school_default_icon.svg';
import districtDefaultIcon from '@/assets/district_default_icon.svg';
import colors from '@/theme/colors';
import { sortBy, uniq } from 'lodash';
import FlyersContext from '@/context/FlyersContext';
import { Subscription } from '@/graphql/types';
import communityIcon from '@/assets/community_icon.svg';
import UserPreferencesContext from '@/context/UserPreferencesContext';
import { useReactiveVar } from '@apollo/client';
import { districtVar, subscriptionsVar } from '@/graphql/state';
import { useNavigate } from 'react-router-dom';
import { AccessibleDivButton, FadeInWrapper } from '@/components';
import styles from './SubscriptionList.styles';

export type SubcriptionOption = Omit<Subscription, 'id' | 'grade'> & {
  id: string;
  description: string;
  dataTestId: string;
};

const MY_SCHOOLS: SubcriptionOption = {
  id: 'all',
  type: 'school',
  name: 'My Schools',
  logo: mySchoolsIcon,
  description: 'all my school flyers',
  dataTestId: 'mySchoolPostings',
};

const SubscriptionList: React.FC = () => {
  const navigate = useNavigate();
  const subscriptions = useReactiveVar(subscriptionsVar);
  const district = useReactiveVar(districtVar);
  const { community } = useContext(UserPreferencesContext);
  const { activeTab } = useContext(FlyersContext);
  const [subscriptionsList, setSubscriptionsList] = useState<
    SubcriptionOption[]
  >([]);
  const isCommunityTab = activeTab === FlyerType.community;

  useEffect(() => {
    if (subscriptions.length > 0 && district) {
      if (activeTab === FlyerType.school) {
        let districtSection;
        if (district) {
          districtSection = {
            name: district?.name,
            type: SubscriptionTypeEnum.district,
            description: 'all district wide flyers',
            id: district?.districtId,
            logo: district?.branding.logoUrl || districtDefaultIcon,
            dataTestId: 'allDistrictPostings',
          };
        }

        const schools = sortBy(
          uniq(subscriptions.filter(sub => sub.type === 'school')),
          s => s.name,
        ).map(s => ({
          ...s,
          type: SubscriptionTypeEnum.school,
          description: 'all school flyers',
          logo: s.logo || schoolDefaultIcon,
          id: s.id.toString(),
          dataTestId: 'allSchoolPostings',
        }));

        const list = [
          ...(district ? [districtSection] : []),
          ...(schools.length > 1 ? [MY_SCHOOLS] : []),
          ...schools,
        ];
        setSubscriptionsList(list);
      } else {
        setSubscriptionsList([
          {
            id: district?.districtId,
            name: `${community.localArea}`,
            logo: communityIcon,
            type: SubscriptionTypeEnum.district,
            description: 'all community flyers',
            dataTestId: 'allCommunityPostings',
          },
        ]);
      }
    }
  }, [subscriptions, district, activeTab]);

  const onSubscriptionClick = (subscription: SubcriptionOption) => {
    navigate(
      `${APP_ROUTES.exploreAllFlyers}?audienceId=${subscription.id}&audienceType=${subscription.type}&tab=${activeTab}&districtId=${district?.districtId}`,
    );
  };

  return (
    <FadeInWrapper className={styles.container}>
      {subscriptionsList.map(subscription => (
        <AccessibleDivButton
          key={subscription.name}
          className={styles.card}
          onClick={() => onSubscriptionClick(subscription)}
          data-testid={subscription.dataTestId}
          aria-label={`Open flyerboard for ${subscription.name}`}
        >
          <div
            className={
              subscription.type === OrgType.school
                ? styles.schoolSection
                : styles.districtSection
            }
            key={subscription.name}
          >
            <div className={styles.section}>
              <div className={styles.imageContainer}>
                {subscription.logo && (
                  <img
                    src={subscription.logo}
                    alt="school logo"
                    className={styles.image}
                  />
                )}
              </div>

              <div className={styles.infoSection}>
                <div className={styles.infoDetails}>
                  <BodyLargeEmphasized
                    nonTranslate={ subscription.id !== 'all' }
                    className={
                      isCommunityTab
                        ? styles.communityName
                        : styles.institutionName
                    }
                  >
                    {subscription.name}
                  </BodyLargeEmphasized>
                  <BodySmall
                    color={colors.slate}
                    className={styles.description}
                  >
                    {subscription.description}
                  </BodySmall>
                </div>
                {!isCommunityTab && (
                  <div>
                    <IconButton aria-label="Go to flyerboard"  tabIndex={-1}>
                      <KeyboardArrowRightIcon sx={{ fontSize: 32 }} />
                    </IconButton>
                  </div>
                )}
              </div>
            </div>
          </div>
        </AccessibleDivButton>
      ))}
    </FadeInWrapper>
  );
};

export default SubscriptionList;
